// React
import { useCallback, useEffect, useState } from 'react'
import { LIMITS } from '@weblite/permission'
// Material
import { Box, Button, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

// Common
import { Label } from '$common/label'
import { TextInput } from '$common/input'
import { signUpUser } from '$logic/auth'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { BottomDrawer } from '$common/bottom-drawer'
import { t } from '$i18n'
import { nothing } from '$func/utils'
import hostConf from '$config/frontend'

const { userConstraints } = LIMITS

const StyledStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.white,
  height: isPhoneOrTablet ? 'fit-content' : '100%',
  padding: '20px 20px',
  zIndex: 2,
  borderRadius: '25px 25px 0 0',
}))

const VITE_SIGNUP_MODE = hostConf.VITE_SIGNUP_MODE || ''
const SHOW_REF_CODE_BOX = VITE_SIGNUP_MODE.toUpperCase().includes('INVITE')

export function SignUp({ isInvited }: { isInvited: boolean }) {
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [referrerCode, setReferrerCode] = useState('')
  const [refCodeError, setRefCodeError] = useState('')
  const [nameIsEmpty, setNameIsEmpty] = useState<boolean>(false)

  const refCodeIsRequired =
    VITE_SIGNUP_MODE === 'Invite&RefCode' ||
    (VITE_SIGNUP_MODE === 'Invite|RefCode' && !isInvited) ||
    VITE_SIGNUP_MODE === 'InviteOnly'

  const onSignUpClick = useCallback(async () => {
    setNameIsEmpty(!name)
    if (!name) return
    const errorCode = await signUpUser(name, lastName, {}, referrerCode)
    if (errorCode) setRefCodeError(t(`signup.errorCode.${errorCode}`))
  }, [name, lastName, referrerCode])

  useEffect(() => {
    setRefCodeError('')
  }, [name, lastName, referrerCode])

  return (
    <BottomDrawer isOpen onClose={nothing}>
      <StyledStack>
        <Box sx={{ display: 'flex', marginBottom: 2 }}>
          <Typography color="neutral1000" variant="h3">
            {t('signup.title')}
          </Typography>
        </Box>

        <Box sx={{ marginBottom: 'auto' }}>
          <Label title={t('user.name')} required />
          <TextInput
            placeholder={t('user.enterName')}
            value={name}
            onChange={setName}
            hasError={nameIsEmpty}
            errorMessage={nameIsEmpty ? t('signup.emptyName') : null}
            maxLength={userConstraints.firstname.maxLength}
          />

          <Label title={t('user.lastName')} />
          <TextInput
            placeholder={t('user.enterLastName')}
            value={lastName}
            onChange={setLastName}
            maxLength={userConstraints.lastname.maxLength}
          />

          {SHOW_REF_CODE_BOX && (
            <>
              <Label title={t('signup.referrerCode')(!refCodeIsRequired)} />
              <TextInput
                placeholder={t('signup.insertReferrerCode')}
                value={referrerCode}
                onChange={setReferrerCode}
                errorMessage={refCodeError}
                hasError={!!refCodeError}
              />
            </>
          )}
        </Box>

        <Button
          variant="contained"
          sx={{ backgroundColor: 'primary500', marginTop: '20px' }}
          size="medium"
          disabled={!name || (refCodeIsRequired && !referrerCode)}
          onClick={onSignUpClick}
        >
          {t('common.register')}
        </Button>
      </StyledStack>
    </BottomDrawer>
  )
}
